import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  fetchSections,
  deleteSection,
  saveSection,
  updateSectionPositions,
} from "@/api/sections";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  PlusCircle,
  Search,
  Edit,
  Trash,
  MoreVertical,
  EyeOff,
  Eye,
  ArrowUpDown,
  ChevronDown,
  Check,
  X,
  LayoutPanelTop,
  BookOpen,
  LibrarySquare,
  Users,
  ImageIcon,
  Filter,
  Loader2,
  BookMarked,
  Plus,
  GripVertical,
  MoveVertical,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { Section, SectionType } from "@/types";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import React from "react";

// Interface for section filters
interface SectionFilters {
  isActive: boolean | undefined;
  type: SectionType | undefined;
  search: string;
  sortBy: string;
  sortDirection: "asc" | "desc";
}

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Helper function to get the icon for a section type
const getSectionTypeIcon = (type: SectionType) => {
  switch (type) {
    case "banner":
      return <ImageIcon className="h-4 w-4" />;
    case "category":
      return <LibrarySquare className="h-4 w-4" />;
    case "book_summary":
      return <BookOpen className="h-4 w-4" />;
    case "collection":
      return <BookMarked className="h-4 w-4" />;
    case "people":
      return <Users className="h-4 w-4" />;
    default:
      return <LayoutPanelTop className="h-4 w-4" />;
  }
};

// Helper function to get human-readable section type
const getSectionTypeName = (type: SectionType) => {
  switch (type) {
    case "banner":
      return "Banners";
    case "category":
      return "Categories";
    case "book_summary":
      return "Books";
    case "collection":
      return "Collections";
    case "people":
      return "People";
    default:
      return type;
  }
};

// Helper function to get badge color for section type
const getSectionTypeColor = (type: SectionType) => {
  switch (type) {
    case "banner":
      return "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300";
    case "category":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
    case "book_summary":
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
    case "collection":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
    case "people":
      return "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300";
  }
};

// Section Row component
interface SectionRowProps {
  section: Section;
  onEdit: (id: string) => void;
  onToggleActive: (id: string, isActive: boolean) => void;
  showDetails?: boolean;
  sortBy?: string;
}

function SectionRow({
  section,
  onEdit,
  onToggleActive,
  showDetails = false,
  sortBy = "position",
}: SectionRowProps) {
  const navigate = useNavigate();
  const isDragEnabled = sortBy === "position";

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: section.id,
    disabled: !isDragEnabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
  };

  const handleRowClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on a button, switch, or drag handle
    if (
      (e.target as HTMLElement).closest("button") ||
      (e.target as HTMLElement).closest('[role="switch"]') ||
      (e.target as HTMLElement).closest("[data-drag-handle]") ||
      isDragging
    ) {
      return;
    }
    navigate(`/admin/sections/${section.id}`);
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      key={section.id}
      className={cn(
        "hover:bg-muted/40 cursor-pointer",
        isDragging ? "opacity-50 bg-accent" : "",
        !section.isActive ? "opacity-60" : ""
      )}
      onClick={handleRowClick}
    >
      <TableCell>
        <div className="flex items-center">
          <div
            {...(isDragEnabled ? attributes : {})}
            {...(isDragEnabled ? listeners : {})}
            className={`${
              isDragEnabled
                ? "cursor-grab active:cursor-grabbing"
                : "cursor-not-allowed opacity-30"
            } p-1 mr-2`}
            data-drag-handle
          >
            <GripVertical className="h-5 w-5 text-muted-foreground" />
          </div>
          <Badge variant="outline" className="bg-background">
            {section.position}
          </Badge>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center space-x-4">
          <div
            className={cn(
              "p-2 rounded-md flex items-center justify-center",
              getSectionTypeColor(section.type)
            )}
          >
            {getSectionTypeIcon(section.type)}
          </div>
          <div>
            <div className="font-medium">{section.title}</div>
            {section.subtitle && (
              <div className="text-sm text-muted-foreground">
                {section.subtitle}
              </div>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <Badge
          variant="outline"
          className={cn("font-medium", getSectionTypeColor(section.type))}
        >
          {getSectionTypeName(section.type)}
        </Badge>
      </TableCell>
      <TableCell className="text-center">
        <Badge variant="secondary" className="font-medium">
          {section.itemCount || 0}
        </Badge>
      </TableCell>
      <TableCell>
        <div className="text-sm text-muted-foreground">
          {format(new Date(section.createdAt), "MMM d, yyyy")}
        </div>
      </TableCell>
      <TableCell className="text-center">
        <div className="flex items-center justify-center">
          <Switch
            checked={section.isActive}
            onCheckedChange={(checked) => onToggleActive(section.id, checked)}
            className="data-[state=checked]:bg-green-500"
            aria-label={
              section.isActive ? "Section is active" : "Section is hidden"
            }
          />
        </div>
      </TableCell>
    </TableRow>
  );
}

// Main Component
export default function SectionsList() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // State
  const [filters, setFilters] = useState<SectionFilters>({
    isActive: undefined,
    type: undefined,
    search: "",
    sortBy: "position",
    sortDirection: "asc",
  });

  // Setup sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Create a DragContext outside component tree
  const [dragActive, setDragActive] = useState(false);

  // Fetch sections
  const {
    data: sectionsData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["sections", filters],
    queryFn: () => fetchSections({ filters }),
  });

  // Delete section mutation
  const deleteMutation = useMutation({
    mutationFn: deleteSection,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sections"] });
    },
  });

  // Update positions mutation
  const updatePositionsMutation = useMutation({
    mutationFn: async (sections: Section[]) => {
      // Convert sections to the format expected by the API
      const positionUpdates = sections.map((section) => ({
        id: section.id,
        position: section.position,
      }));

      // Call the API to update positions
      return updateSectionPositions(positionUpdates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sections"] });
      toast({
        title: "Positions updated",
        description: "Section positions have been updated successfully.",
      });
    },
    onError: (error) => {
      console.error("Error updating positions:", error);
      toast({
        title: "Error updating positions",
        description: "There was an error updating the section positions.",
        variant: "destructive",
      });
    },
  });

  // Handlers
  const handleCreateSection = () => {
    navigate("/admin/sections/new");
  };

  const handleEditSection = (id: string) => {
    navigate(`/admin/sections/${id}/edit`);
  };

  const handleDeleteSection = async (id: string, title: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      toast({
        title: "Section deleted",
        description: `The section "${title}" has been deleted.`,
      });
    } catch (err) {
      console.error("Failed to delete section:", err);
      toast({
        title: "Failed to delete section",
        description: "An error occurred while deleting the section.",
        variant: "destructive",
      });
    }
  };

  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      // Optimistically update UI
      queryClient.setQueryData(
        ["sections", filters],
        (oldData: Section[] | undefined) => {
          if (!oldData) return oldData;

          return oldData.map((section: Section) =>
            section.id === id ? { ...section, isActive } : section
          );
        }
      );

      // Find the section to update
      const sectionToUpdate = sectionsData?.find((s) => s.id === id);

      if (!sectionToUpdate) {
        throw new Error("Section not found");
      }

      // Call API to update section
      await saveSection({
        ...sectionToUpdate,
        isActive,
      });

      // Show success message
      toast({
        title: isActive ? "Section activated" : "Section hidden",
        description: `"${sectionToUpdate.title}" is now ${
          isActive ? "active" : "hidden"
        }`,
      });
    } catch (error) {
      console.error("Error toggling section status:", error);

      // Revert the optimistic update on error
      queryClient.invalidateQueries({ queryKey: ["sections"] });

      toast({
        title: "Error updating section",
        description: "There was a problem updating the section status",
        variant: "destructive",
      });
    }
  };

  // Handle the end of a drag event
  const handleDragEnd = async (event: DragEndEvent) => {
    // Skip processing if not sorting by position
    if (filters.sortBy !== "position") return;

    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    if (sectionsData) {
      try {
        // Find the items we're swapping
        const oldIndex = sectionsData.findIndex(
          (section) => section.id === active.id
        );
        const newIndex = sectionsData.findIndex(
          (section) => section.id === over.id
        );

        if (oldIndex !== -1 && newIndex !== -1) {
          // Create a new array with the moved item
          const newSections = arrayMove(sectionsData, oldIndex, newIndex);

          // Update positions based on array index with sequential values
          const sectionsWithNewPositions = newSections.map((section, idx) => ({
            ...section,
            position: idx + 1, // Using sequential positions: 1, 2, 3, etc.
          }));

          // Optimistically update the UI
          queryClient.setQueryData(
            ["sections", filters],
            sectionsWithNewPositions
          );

          // Call the API to save the new positions
          updatePositionsMutation.mutate(sectionsWithNewPositions);
        }
      } catch (err) {
        console.error("Error updating positions:", err);
        // Revert to the original state on error
        queryClient.invalidateQueries({ queryKey: ["sections"] });
      }
    }
  };

  const handleFilterChange = (
    name: keyof SectionFilters,
    value: SectionFilters[keyof SectionFilters]
  ) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetFilters = () => {
    setFilters({
      isActive: undefined,
      type: undefined,
      search: "",
      sortBy: "position",
      sortDirection: "asc",
    });
  };

  const handleSort = (column: string) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: column,
      sortDirection:
        prev.sortBy === column && prev.sortDirection === "asc" ? "desc" : "asc",
    }));
  };

  // Helper to get sort indicator
  const getSortIndicator = (column: string) => {
    if (filters.sortBy !== column) return null;
    return (
      <ArrowUpDown
        className={`h-4 w-4 ml-1 ${
          filters.sortDirection === "desc" ? "transform rotate-180" : ""
        }`}
      />
    );
  };

  // Add section button for the action button in AdminLayout
  const addSectionButton = (
    <Button onClick={handleCreateSection}>
      <Plus className="mr-2 h-4 w-4" />
      Add Section
    </Button>
  );

  // Handle error state
  if (isError) {
    return (
      <AdminLayout title="Sections" actionButton={addSectionButton}>
        <Card className="mt-6">
          <CardContent className="p-6">
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <X className="h-12 w-12 text-destructive mb-4" />
              <h2 className="text-2xl font-bold mb-2">
                Error Loading Sections
              </h2>
              <p className="text-muted-foreground mb-6 max-w-md">
                {error instanceof Error
                  ? error.message
                  : "An error occurred while loading sections. Please try again."}
              </p>
              <Button
                onClick={() =>
                  queryClient.invalidateQueries({ queryKey: ["sections"] })
                }
                className="gap-2"
              >
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Sections" actionButton={addSectionButton}>
      {/* Put DndContext at the top level, outside of the table structure */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className="h-full flex-1 flex-col space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>Filters</CardTitle>
              <CardDescription>
                Filter the sections list by type, status, or search term
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4 md:flex-row md:items-end">
                <div className="grid gap-2 flex-1">
                  <Label htmlFor="search-sections">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="search-sections"
                      placeholder="Search by title..."
                      className="pl-8"
                      value={filters.search}
                      onChange={(e) =>
                        handleFilterChange("search", e.target.value)
                      }
                    />
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="section-type">Type</Label>
                  <Select
                    value={filters.type || "all"}
                    onValueChange={(value) =>
                      handleFilterChange(
                        "type",
                        value === "all" ? undefined : value
                      )
                    }
                  >
                    <SelectTrigger id="section-type" className="w-[180px]">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="banner">Banners</SelectItem>
                      <SelectItem value="category">Categories</SelectItem>
                      <SelectItem value="book_summary">
                        Books
                      </SelectItem>
                      <SelectItem value="collection">Collections</SelectItem>
                      <SelectItem value="people">People</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="section-status">Status</Label>
                  <Select
                    value={
                      filters.isActive === undefined
                        ? "all"
                        : filters.isActive
                        ? "active"
                        : "inactive"
                    }
                    onValueChange={(value) =>
                      handleFilterChange(
                        "isActive",
                        value === "all"
                          ? undefined
                          : value === "active"
                          ? true
                          : false
                      )
                    }
                  >
                    <SelectTrigger id="section-status" className="w-[180px]">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={resetFilters}
                >
                  <Filter className="h-4 w-4" />
                  Reset
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="rounded-md border overflow-hidden">
            {filters.sortBy !== "position" && (
              <div className="bg-muted/50 p-3 flex items-center justify-center text-sm text-muted-foreground border-b">
                <MoveVertical className="h-4 w-4 mr-2 text-muted-foreground" />
                Drag and drop reordering is only available when sorting by
                Position. Click on the "Position" column header to enable drag
                and drop functionality.
              </div>
            )}
            <Table>
              <TableHeader className="bg-muted/50">
                <TableRow className="hover:bg-muted/50">
                  <TableHead
                    className="cursor-pointer w-[120px]"
                    onClick={() => handleSort("position")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Position</span>
                      {getSortIndicator("position")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer w-[300px]"
                    onClick={() => handleSort("title")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Section</span>
                      {getSortIndicator("title")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer"
                    onClick={() => handleSort("type")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Type</span>
                      {getSortIndicator("type")}
                    </div>
                  </TableHead>
                  <TableHead className="text-center">
                    <div className="flex items-center justify-center">
                      <div className="flex items-center space-x-1">
                        <span>Items</span>
                      </div>
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer"
                    onClick={() => handleSort("created_at")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Created</span>
                      {getSortIndicator("created_at")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer text-center w-[100px]"
                    onClick={() => handleSort("is_active")}
                  >
                    <div className="flex items-center justify-center">
                      <Eye className="h-4 w-4 mr-1" />
                      <span>Status</span>
                      {getSortIndicator("is_active")}
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center">
                      <div className="flex justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                      </div>
                    </TableCell>
                  </TableRow>
                ) : sectionsData?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <LayoutPanelTop className="h-8 w-8 text-muted-foreground" />
                        <span className="text-muted-foreground font-medium">
                          No sections found
                        </span>
                        <span className="text-sm text-muted-foreground">
                          Get started by creating your first section
                        </span>
                        {(filters.search ||
                          filters.type ||
                          filters.isActive !== undefined) && (
                          <Button
                            variant="link"
                            onClick={resetFilters}
                            className="mt-2"
                          >
                            Clear filters
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  <SortableContext
                    items={sectionsData.map((section) => section.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {sectionsData.map((section) => (
                      <SectionRow
                        key={section.id}
                        section={section}
                        onEdit={handleEditSection}
                        onToggleActive={handleToggleActive}
                        sortBy={filters.sortBy}
                      />
                    ))}
                  </SortableContext>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </DndContext>
    </AdminLayout>
  );
}
